<template>
    <div :id="id" :class="{
        markdown: markdown,
        article: !markdown,
        readonly: readOnly,
        'font-s': $store.state.font_size == 1,
        'font-m': $store.state.font_size == 2,
        'font-l': $store.state.font_size == 3,
        'font-x': $store.state.font_size == 4,
        'font-h': $store.state.font_size == 5,
    }"
    @click="click"
    @keydown="handleKeydown"></div>
</template>

<script>
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Table from "@editorjs/table";
import SimpleImage from "@editorjs/simple-image";
import Embed from '@editorjs/embed';
import Underline from '@editorjs/underline';
import Checklist from '@editorjs/checklist';
import Delimiter from '@editorjs/delimiter';
import Marker from "@editorjs/marker";
import ColorPlugin from "editorjs-text-color-plugin";
import TextVariantTune from '@editorjs/text-variant-tune';
import AlignmentTuneTool from "editorjs-text-alignment-blocktune";
class GyazoImage extends SimpleImage
{
    onPaste(ev) {
        if (ev.detail.key == "gyazo") {
            this.data = {
                url: ev.detail.data + "/max_size/1000"
            };
        } else {
            super.onPaste(ev);
        }
    }
    static get pasteConfig() {
        return {
            patterns: {
                image: /https?:\/\/\S+\.(gif|jpe?g|tiff|png|webp)$/i,
                gyazo: /https?:\/\/gyazo\.com\/[a-z\d]+/i,
                photo: /https?:\/\/lh3\.googleusercontent\.com\/[a-z\d_\-=\?\/]+/i,
            },
            tags: [ 'img' ],
            files: {
                mimeTypes: [ 'image/*' ],
            },
        };
    }

    onDropHandler(file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise(resolve => {
            reader.onload = (event) => {
                if (event.target.result.match(/^data:image\/webp;base64/)) {
                    // webpは変換しない
                    resolve({
                        url: event.target.result,
                        caption: file.name,
                    });
                } else {
                    // webp以外はwebpに変換する
                    axios.post("/mypage/image/convert", {
                        base64: event.target.result
                    }).then(res => {
                        resolve({
                            url: "data:image/webp;base64," + res.data,
                            caption: file.name,
                        });
                    }).catch(err => {
                        // 失敗したらそのまま埋め込みする
                        resolve({
                            url: event.target.result,
                            caption: file.name,
                        });
                     });;
                }
            };
        });
    }
}
export default {
    props: {
        id: {
            default: "block-editor"
        },
        content: {
            default: null,
        },
        lock: {
            default: false
        },
        readOnly: {
            default: false,
        },
        minHeight: {
            default: 300,
        },
        markdown: {
            default: true,
        },
        placeholder: {
            default: "",
        },
        keyword: {
            default: null,
        }
    },
    data() {
        return {
            editor: null,
            undo: [],
            redo: [],
            isRendering: false,
            MAX_HISTORY: 50,
            ready_at: 0,
            readOnlyStatus: false,
        }
    },
    methods: {
        init() {
            this.readOnlyStatus = this.readOnly;
            const config = {
                holder: this.id,
                readOnly: this.readOnly,
                placeholder: this.placeholder,
                tools: {
                    header: {
                        class: Header,
                        shortcut: "CMD+SHIFT+P",
                        inlineToolbar: true,
                        tunes: ['textAlign'],
                    },
                    image: GyazoImage,
                    list: {
                        class: List,
                        inlineToolbar: true,
                        config: {
                            defaultStyle: "unordered"
                        }
                    },
                    table: {
                        class: Table,
                        inlineToolbar: true,
                        config: {
                            rows: 2,
                            cols: 3,
                        }
                    },
                    embed: {
                        class: Embed,
                        config: {
                            services: {
                                youtube: true,
                                facebook: true,
                                instagram: true,
                                twitter: true,
                                "twitch-video": true,
                                "twitch-channel": true,
                                miro: true,
                                vimeo: true,
                                imgur: true,
                                codepen: true,
                                ogp: {
                                    regex: /(https?:\/\/[\w!?\/+\-_~;.,*&@#$%()'[\]]+)/,
                                    embedUrl: '/embed?url=<%= remote_id %>',
                                    html: "<iframe height='150' scrolling='no' frameborder='no' allowtransparency='true' allowfullscreen='true' style='width: 100%;'></iframe>",
                                    height: 150,
                                    width: 600,
                                    id: (groups) => groups.join('')
                                }
                            }
                        }
                    },
                    underline: {
                        class: Underline,
                        shortcut: "CMD+SHIFT+U",
                    },
                    checklist: {
                        class: Checklist,
                        inlineToolbar: true,
                    },
                    delimiter: Delimiter,
                    Marker: {
                        class: Marker,
                        shortcut: 'CMD+SHIFT+M',
                    },
                    textVariant: TextVariantTune,
                    paragraph: {
                        tunes: [
                            'textVariant',
                            'textAlign',
                        ],
                    },
                    textAlign: {
                        class: AlignmentTuneTool,
                        config: {
                            default: "left",
                        },
                    },
                    Color: {
                        class: ColorPlugin,
                        config: {
                            colorCollections: [
                                '#1d1e1f',
                                '#ff1300',
                                '#ec7878',
                                '#9c27b0',
                                '#673ab7',
                                '#3f51b5',
                                '#0070ff',
                                '#03a9f4',
                                '#00bcd4',
                                '#4caf50',
                                '#8bc34a',
                                '#cddc39',
                                '#fff'
                            ],
                            defaultColor: '#ff1300',
                            type: 'text',
                        }
                    },
                },
                i18n: {
                    messages: {
                        ui: {
                            "blockTunes": {
                                "toggler": {
                                    "Click to tune": "クリックして調整",
                                    "or drag to move": "ドラッグして移動"
                                },
                            },
                            "inlineToolbar": {
                                "converter": {
                                    "Convert to": "変換"
                                }
                            },
                            "toolbar": {
                                "toolbox": {
                                    "Add": "追加"
                                }
                            }
                        },
                        toolNames: {
                            "Text": "テキスト",
                            "Heading": "見出し",
                            "List": "リスト",
                            "Warning": "警告",
                            "Checklist": "チェックリスト",
                            "Quote": "引用",
                            "Code": "コード",
                            "Delimiter": "区切り",
                            "Raw HTML": "HTMLコード",
                            "Table": "テーブル",
                            "Link": "リンク",
                            "Marker": "マーカー",
                            "Bold": "太字",
                            "Italic": "斜体",
                            "InlineCode": "インラインコード",
                            "Underline": "下線",
                        },
                        tools: {
                        },
                        blockTunes: {
                            "delete": {
                                "Delete": "削除"
                            },
                            "moveUp": {
                                "Move up": "上へ"
                            },
                            "moveDown": {
                                "Move down": "下へ"
                            },
                            "textVariant": {
                                "Details": "詳細",
                                "Call-out": "強調",
                                "Citation": "例文",
                            },
                        },
                    },
                },
                tunes: ['textVariant'],
                data: this.contentFormatted,
                minHeight: this.minHeight,
                onChange: (api, ev) => {
                    if ((moment().unix() - this.ready_at) <= 2) return;
                    if (ev.type === "block-added") return;
                    if (!this.isRendering) {
                        this.addUndo(ev);
                    }
                    if (!this.readOnly) {
                        this.$emit("change", ev);
                    }
                },
                onReady: (api, ev) => {
                    this.ready_at = moment().unix();
                    this.$emit("ready");
                }
            };
            this.editor = new EditorJS(config);
        },
        refresh(callback = null) {
            this.editor.isReady.then(() => {
                if (!this.content) {
                    this.editor.blocks.clear();
                    this.initUndo();
                    if (callback) callback();
                } else {
                    this.editor.blocks.render(this.contentFormatted).then(() => {
                        this.initUndo();
                        if (callback) callback();
                    });
                }
            });
        },
        save() {
            if (this.readOnlyStatus) {
                return new Promise((resolve) => {
                    resolve(this.content);
                });
            }
            return this.editor.save();
        },
        initUndo() {
            this.undo = [];
            this.addUndo();
        },
        async addUndo(ev = null) {
            if (this.readOnly) return;
            this.redo = [];
            const saveData = await this.editor.save();
            this.undo.push({
                blocks: _.cloneDeep(saveData),
                index: ev?.detail ? ev.detail.index : 0,
                scrollTop: $("#" + this.id)[0].scrollTop,
            });
            while (this.undo.length > this.MAX_HISTORY) {
                this.undo.shift();
            }
            if (ev) {
                this.$emit("saved", saveData);
            }
        },
        handleKeydown(e) {
            if (!e.isComposing) {
                if (e.metaKey || e.ctrlKey) {
                    if (e.code == "KeyZ") {
                        e.preventDefault();
                        this.execUndo();
                    } else if (e.code == "KeyY") {
                        e.preventDefault();
                        this.execRedo();
                    }
                }
            }
        },
        execUndo() {
            if (!this.isRendering && this.undo.length > 1) {
                this.isRendering = true;
                const curr = this.undo.pop();
                const undo = this.undo[this.undo.length - 1];
                this.editor.blocks.render(undo.blocks).then(() => {
                    this.redo.push(curr);
                    this.isRendering = false;
                    $("#" + this.id)[0].scrollTop = curr.scrollTop;
                    this.editor.caret.focus();
                    this.$emit("change", new CustomEvent("undo"));
                });
            }
        },
        execRedo() {
            if (!this.isRendering && this.redo.length > 0) {
                this.isRendering = true;
                const redo = this.redo.pop();
                this.editor.blocks.render(redo.blocks).then(() => {
                    this.undo.push(redo);
                    this.isRendering = false;
                    $("#" + this.id)[0].scrollTop = redo.scrollTop;
                    this.editor.caret.focus();
                    this.$emit("change", new CustomEvent("redo"));
                });
            }
        },
        makeId(len = 10) {
            const alphabet = 'abcdefghijklmnopqrstuvwxyz';
            const alphabetUpper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ_';
            const numbers = '0123456789';

            const passBase  = alphabet + alphabetUpper + numbers;

            let password = '';

            for (let i = 0; i < len; i++) {
                password += passBase.charAt(Math.floor(Math.random() * passBase.length));
            }

            return password;
        },
        foundKeyword() {
            if (this.keyword) {
                const regex = new RegExp("(" + this.keyword.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&') + ")", "ug");
                const replace = "<span class='found'>$1</span>";
                for (const block of this.content.blocks) {
                    if (block.data.text) {
                        block.data.text = block.data.text.replace(regex, replace);
                    } else if (block.data.items) {
                        // リスト
                        for (const i in block.data.items) {
                            if (block.data.items[i].text) {
                                block.data.items[i].text = block.data.items[i].text.replace(regex, replace);
                            } else {
                                block.data.items[i] = block.data.items[i].replace(regex, replace);
                            }
                        }
                    } else if (block.data.content) {
                        for (const r in block.data.content) {
                            const row = block.data.content[r];
                            for (const c in row) {
                                row[c] = row[c].replace(regex, replace);
                            }
                        }
                    } else if (block.data.caption) {
                        block.data.caption = block.data.caption.replace(regex, replace);
                    }
                }
            }
        },
        click() {
            if (this.readOnly) {
                this.$emit("clickedOnReadOnly");
            }
        }
    },
    watch: {
        content(n, o) {
            if (this.readOnly) {
                if (o && n.time !== o.time) {
                    this.refresh();
                }
            }
            this.ready_at = moment().unix();
        },
        readOnly(n, o) {
            this.readOnlyStatus = !this.readOnlyStatus;
            this.editor.readOnly.toggle();
        }
    },
    computed: {
        contentFormatted() {
            const content = {
                "time": moment().unix(),
                "blocks": [{type:"paragraph",data:{}}],
                "version": "2.25.0",
            };
            if (!this.content) {
                return content;
            };
            if (typeof this.content === "object") {
                if (!this.content.blocks || !this.content.blocks.length) {
                    return content;
                }
                this.foundKeyword();
                return this.content;
            }
            content.blocks = [];
            const rows = this.content.split(/[\r\n]+/);
            rows.push("");
            let matched, r;
            let list = [];
            let isOrdered = false;
            const listRegex = new RegExp("^(([\*\-]|1\.) |・)");
            for (const row of rows) {
                if (matched = row.match(listRegex)) {
                    if (row.match(/^1\. /)) {
                        isOrdered = true;
                    } else {
                        isOrdered = false;
                    }
                    list.push(row.replace(listRegex, '')
                        .replace(/\*{3}([^\*]+)\*{3}/g, "<i>$1</i>")
                        .replace(/\*{2}([^\*]+)\*{2}/g, "<b>$1</b>"));
                    continue;
                } else if (list.length > 0) {
                    r = {
                        "id": this.makeId(),
                        "type": "list",
                        "data": {
                            "style": isOrdered ? "ordered" : "unordered",
                            "items": list
                        }
                    };
                    content.blocks.push(r);
                    list = [];
                }
                if (matched = row.match(/^#{1,6}/)) {
                    r = {
                        "id": this.makeId(),
                        "type": "header",
                        "data": {
                            "text": row.replace(/^#+/, ''),
                            "level": matched[0].length
                        }
                    };
                } else {
                    r = {
                        "id": this.makeId(),
                        "type": "paragraph",
                        "data": {
                            "text": row
                                .replace(/\*{3}([^\*]+)\*{3}/g, "<i>$1</i>")
                                .replace(/\*{2}([^\*]+)\*{2}/g, "<b>$1</b>")
                        }
                    };
                }
                content.blocks.push(r);
            }
            return content;
        },
    },
    mounted() {
        this.init();
    },
    destroy() {
        this.editor.destroy();
    }
}
</script>